import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import './ArrangeQuestion.css';

// Create audio element with public URL
const clickAudio = new Audio('/sounds/click.mp3');

// Preload the audio file
clickAudio.load();

const ItemTypes = {
  ITEM: 'item'
};

const DraggableItem = ({ id, text, image, index, moveItem, removeItem }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.ITEM,
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.ITEM,
    hover(draggedItem) {
      if (draggedItem.index !== index) {
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const handleKeyDown = (e) => {
    // Handle space or enter to start dragging
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      const node = e.currentTarget;
      node.click(); // Trigger the drag start
    }
  };

  return (
    <button
      ref={(node) => drag(drop(node))}
      className={`draggable-item ${isDragging ? 'dragging' : ''} ${isOver ? 'drag-over' : ''}`}
      aria-pressed={isDragging}
      aria-label={`${text}. Drag to reorder. Press space or enter to start dragging.`}
      onKeyDown={handleKeyDown}
      data-dragging={isDragging}
      type="button"
    >
      <div className="item-content">
        <div className="item-number">{index + 1}</div>
        {image && (
          <img 
            src={image} 
            alt="" 
            className="item-image"
            aria-hidden="true"
          />
        )}
        <div className="item-text">{text}</div>
      </div>
      <button 
        className="remove-btn"
        onClick={(e) => {
          e.stopPropagation();
          removeItem(id);
        }}
        aria-label={`Remove ${text}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            removeItem(id);
          }
        }}
      >
        ⨉
      </button>
    </button>
  );
};

// Helper function to compare IDs consistently
const isIdInArray = (id, idArray) => {
  if (!idArray || !Array.isArray(idArray)) return false;
  return idArray.some(itemId => String(itemId) === String(id));
};

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const ArrangeQuestion = ({ 
  question = { text: '', image: '', options: [] }, 
  items: propItems = [], 
  onArrange,
  isSubmitted = false,
  correctOrder = []
}) => {
  // Track dragged item index for styling
  const [draggedItem, setDraggedItem] = useState(null);
  
  // Create a stable reference to the items
  const items = useMemo(() => 
    Array.isArray(propItems) ? [...propItems] : []
  , [propItems]);
  
  // Reset draggedItem when question changes
  useEffect(() => {
    setDraggedItem(null);
  }, [question.id]); // Reset when question ID changes
  
  // Shuffle the options only once when the question changes
  const shuffledOptions = useMemo(() => {
    if (!question.options || !Array.isArray(question.options)) {
      console.warn('No options provided for arrange question');
      return [];
    }
    // Create a deep copy of the options to avoid modifying the original
    const optionsCopy = JSON.parse(JSON.stringify(question.options));
    return shuffleArray(optionsCopy);
  }, [question.options]);
  
  // Update parent when items change
  const updateItems = useCallback((newItems) => {
    // Only update if the items have actually changed
    if (JSON.stringify(newItems) !== JSON.stringify(propItems)) {
      onArrange(newItems);
    }
  }, [propItems, onArrange]);

  const removeItem = useCallback((id) => {
    // Don't remove items if already submitted
    if (isSubmitted) return;
    
    // Create new items array without the removed item
    const newItems = items.filter(item => String(item.id) !== String(id));
    updateItems(newItems);
    clickAudio.play().catch(e => console.log('Audio play failed:', e));
  }, [items, isSubmitted, updateItems]);

  // Filter out items that are already in the arrangement
  const availableItems = useMemo(() => {
    if (!Array.isArray(shuffledOptions) || !Array.isArray(items)) {
      return [];
    }
    return shuffledOptions.filter(opt => {
      if (!opt || typeof opt !== 'object') return false;
      const optId = String(opt.id);
      return !items.some(item => item && item.id !== undefined && String(item.id) === optId);
    });
  }, [shuffledOptions, items]);

  const handleItemClick = useCallback((item) => {
    // Don't add items if already submitted
    if (isSubmitted || !item) return;
    
    try {
      const itemId = String(item.id);
      
      // Don't add if already in the list
      if (items.some(i => i && i.id !== undefined && String(i.id) === itemId)) {
        console.log('Item already in list:', itemId);
        return;
      }
      
      // Create a new item with all the properties from the selected item
      const newItem = { 
        ...item,
        // Ensure we have a text property
        text: item.text || String(item.id)
      };
      
      const newItems = [...items, newItem];
      updateItems(newItems);
      clickAudio.play().catch(e => console.log('Audio play failed:', e));
    } catch (error) {
      console.error('Error handling item click:', error);
    }
  }, [items, isSubmitted, updateItems]);

  const moveItem = useCallback((fromIndex, toIndex) => {
    if (isSubmitted) return;
    
    const newItems = [...items];
    const [movedItem] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, movedItem);
    
    updateItems(newItems);
  }, [items, isSubmitted, updateItems]);
  
  const handleDragStart = useCallback((e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
    e.dataTransfer.setDragImage(e.currentTarget, 20, 20);
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    if (isSubmitted || draggedItem === null) return;
    
    const draggedOverItem = index;
    
    if (draggedItem === draggedOverItem) {
      return;
    }
    
    moveItem(draggedItem, draggedOverItem);
    setDraggedItem(draggedOverItem);
  }, [draggedItem, isSubmitted, moveItem]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDraggedItem(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
  }, []);

  // Get the class for an arranged item based on its correctness
  const getItemClass = useCallback((item, index) => {
    const classes = ['arranged-item'];
    
    if (isSubmitted && correctOrder?.length) {
      // Get the position class for an item
      const getPositionClass = (item, idx) => {
        if (!isSubmitted || !correctOrder?.length) return '';
        
        const isInCorrectPosition = String(item.id) === String(correctOrder[idx]);
        if (isInCorrectPosition) return 'correct';
        if (isIdInArray(item.id, correctOrder)) return 'wrong-position';
        return 'incorrect';
      };
      
      const positionClass = getPositionClass(item, index);
      if (positionClass) {
        classes.push(positionClass);
      }
    }
    
    if (draggedItem === index) {
      classes.push('dragging');
    }
    
    if (!isSubmitted) {
      classes.push('interactive');
    }
    
    return classes.join(' ');
  }, [isSubmitted, correctOrder, draggedItem]);

  return (
    <div className={`arrange-container ${isSubmitted ? 'submitted' : ''}`}>
      <div className="arrange-layout">
        {/* Available items row */}
        <div className="available-items-row">
          <h4>Options: (Click to add to arrangement)</h4>
          <div className="available-items-grid">
            {availableItems.map((item) => (
              <button
                key={`available-${item.id}`}
                className="available-item"
                onClick={() => handleItemClick(item)}
                onKeyDown={(e) => e.key === 'Enter' && handleItemClick(item)}
                aria-label={`Add ${item.text} to arrangement`}
              >
                {item.image ? (
                  <img src={item.image} alt="" className="available-item-image" />
                ) : (
                  <span className="available-item-text">{item.text}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Arranged items row */}
        <div className="arranged-items-row">
          <h4>Your arrangement: (Click ✕ to remove or Drag to rearrange)</h4>
          <div className="arrange-items">
            {items.length > 0 ? (
              items.map((item, index) => {
                const itemOrder = correctOrder.indexOf(String(item.id)) + 1;
                const itemAriaLabel = isSubmitted 
                  ? `${item.text}. Correct order: ${itemOrder}`
                  : `${item.text}. Press space or enter to remove.`;
                
                // Handle drag start
                const handleItemDragStart = (e) => !isSubmitted && handleDragStart(e, index);
                
                // Handle drag over
                const handleItemDragOver = (e) => !isSubmitted && handleDragOver(e, index);
                
                // Handle drop
                const handleItemDrop = (e) => {
                  !isSubmitted && handleDrop(e, index);
                  clickAudio.play();
                }
                
                // Handle key down
                const handleKeyDown = (e) => {
                  if (!isSubmitted && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    removeItem(item.id);
                  }
                };
                
                // Handle click
                const handleClick = (e) => {
                  if (!isSubmitted) {
                    e.preventDefault();
                    removeItem(item.id);
                    clickAudio.play();
                  }
                };
                
                return (
                  <button
                    key={item.id}
                    className={getItemClass(item, index)}
                    disabled={isSubmitted}
                    draggable={!isSubmitted}
                    onDragStart={handleItemDragStart}
                    onDragOver={handleItemDragOver}
                    onDrop={handleItemDrop}
                    onDragEnd={handleDragEnd}
                    onKeyDown={handleKeyDown}
                    aria-label={itemAriaLabel}
                    onClick={handleClick}
                    type="button"
                  >
                    {item.image ? (
                      <img src={item.image} alt="" className="arranged-item-image" />
                    ) : (
                      <div className="arranged-item-text">
                        {item.text || item.id} {/* Fallback to id if text is not available */}
                      </div>
                    )}
                    {!isSubmitted && (
                      <button 
                        className="remove-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeItem(item.id);
                          clickAudio.play();
                        }}
                        aria-label={`Remove ${item.text} from arrangement`}
                      >
                        ✕
                      </button>
                    )}
                  </button>
                );
              })
            ) : (
              <div className="empty-arrangement">
                <p>{isSubmitted ? 'No items were arranged.' : 'Click on items above to add them here'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component to provide DnD context
const ArrangeQuestionWithProvider = (props) => (
  <DndProvider backend={HTML5Backend}>
    <ArrangeQuestion {...props} />
  </DndProvider>
);

DraggableItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  text: PropTypes.string.isRequired,
  image: PropTypes.string,
  index: PropTypes.number.isRequired,
  moveItem: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired,
};

ArrangeQuestion.propTypes = {
  question: PropTypes.shape({
    text: PropTypes.string,
    image: PropTypes.string,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        text: PropTypes.string,
        image: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      text: PropTypes.string,
      image: PropTypes.string,
    })
  ),
  onArrange: PropTypes.func.isRequired,
  isSubmitted: PropTypes.bool,
  correctOrder: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
};

export default ArrangeQuestionWithProvider;
